<template>
  <v-dialog v-bind:value="show" @click:outside="$emit('close')" fullscreen scrollable>
    <v-card>
      <v-card-title class="headline">
        <v-icon left color="info">help_outline</v-icon>
        Guía de Cuentas Tesorería
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="max-height: 75vh;">

        <!-- TIPOS DE CUENTA -->
        <p class="subtitle-1 font-weight-bold mt-4 mb-2">Tipos de cuenta (<code>account_type</code>)</p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Descripción / Cuándo usarlo</th>
              <th>Admite sub-tipo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>B</code></td>
              <td>Banco</td>
              <td>Cuenta bancaria (Caja de Ahorro o Cuenta Corriente). Puede tener chequeras asociadas si el sub-tipo es <code>CC</code>.</td>
              <td>Sí — <code>CA</code>, <code>CC</code></td>
            </tr>
            <tr>
              <td><code>C</code></td>
              <td>Cheques</td>
              <td>Chequera asociada a una cuenta bancaria de tipo <code>B/CC</code>. Debe tener una <strong>cuenta padre</strong>. Se usa para registrar cheques propios emitidos.</td>
              <td>Sí — <code>CD</code></td>
            </tr>
            <tr>
              <td><code>T</code></td>
              <td>Tarjeta</td>
              <td>Tarjeta de crédito o débito corporativa.</td>
              <td>No</td>
            </tr>
            <tr>
              <td><code>E</code></td>
              <td>Efectivo</td>
              <td>Caja chica o dinero en efectivo. Representa fondos físicos disponibles.</td>
              <td>No</td>
            </tr>
            <tr>
              <td><code>O</code></td>
              <td>Otras</td>
              <td>Cuentas auxiliares o de contrapartida (Deudores por Ventas, Acreedores por Compras, cuentas transitorias, etc.). No representan dinero líquido.</td>
              <td>No</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="my-4"></v-divider>

        <!-- SUB-TIPOS -->
        <p class="subtitle-1 font-weight-bold mb-2">Sub-tipos de cuenta (<code>sub_account_type</code>)</p>
        <p class="body-2 mb-2">Los sub-tipos solo aplican cuando el tipo de cuenta es <strong>Banco (<code>B</code>)</strong> o <strong>Cheques (<code>C</code>)</strong>.</p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Tipo de cuenta padre</th>
              <th>Descripción</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>CA</code></td>
              <td>Caja de Ahorro</td>
              <td><code>B</code> — Banco</td>
              <td>Cuenta de ahorro bancaria. No permite crear chequeras.</td>
            </tr>
            <tr>
              <td><code>CC</code></td>
              <td>Cuenta Corriente</td>
              <td><code>B</code> — Banco</td>
              <td>Cuenta corriente bancaria. Permite dar de alta chequeras (cuentas hijas de tipo <code>C/CD</code>). Es la cuenta que se debita cuando se deposita un cheque propio.</td>
            </tr>
            <tr>
              <td><code>CD</code></td>
              <td>Cheque</td>
              <td><code>C</code> — Cheques</td>
              <td>Sub-tipo de una chequera. La cuenta hija queda vinculada a la cuenta corriente bancaria (<code>B/CC</code>) seleccionada como cuenta padre.</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="my-4"></v-divider>

        <!-- REPRESENTA FONDOS -->
        <p class="subtitle-1 font-weight-bold mb-2">¿Qué significa "Representa fondos"?</p>
        <v-simple-table dense>
          <tbody>
            <tr>
              <td width="120"><strong>SI</strong></td>
              <td>
                La cuenta contiene dinero real o líquido (Banco, Caja Chica, Cheques en Cartera, etc.).
                Estas cuentas aparecen en el resumen de fondos disponibles de la empresa y se consideran al calcular el saldo de tesorería.
              </td>
            </tr>
            <tr>
              <td><strong>NO</strong></td>
              <td>
                Cuenta auxiliar o de contrapartida. No representa dinero disponible.
                Ejemplos: Deudores por Ventas, Acreedores por Compras, Valores a Depositar, cuentas transitorias internas.
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="my-4"></v-divider>

        <!-- CUENTAS POR DEFECTO -->
        <p class="subtitle-1 font-weight-bold mb-2">Cuentas por defecto por emisor</p>
        <p class="body-2 mb-2">Al dar de alta un emisor, el sistema crea automáticamente las siguientes cuentas. Su código incluye un sufijo numérico con el ID del emisor (ej.: <code>CCH-0001</code>).</p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Código base</th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Rep. fondos</th>
              <th>Uso</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>CCH</code></td>
              <td>Cheques en Cartera</td>
              <td>Cheques (<code>C</code>)</td>
              <td>SI</td>
              <td>Cheques de tercero en posesión sin imputar</td>
            </tr>
            <tr>
              <td><code>CCI</code></td>
              <td>Caja Chica</td>
              <td>Efectivo (<code>E</code>)</td>
              <td>SI</td>
              <td>Efectivo físico en caja</td>
            </tr>
            <tr>
              <td><code>DXV</code></td>
              <td>Deudores por Ventas</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Contrapartida al recibir un cheque de tercero en un cobro</td>
            </tr>
            <tr>
              <td><code>AXC</code></td>
              <td>Acreedores por Compras</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Contrapartida al entregar un cheque propio como pago</td>
            </tr>
            <tr>
              <td><code>OI</code></td>
              <td>Otros Ingresos</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Ingresos sin clasificar en movimientos manuales</td>
            </tr>
            <tr>
              <td><code>OE</code></td>
              <td>Otros Egresos</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Egresos sin clasificar en movimientos manuales</td>
            </tr>
            <tr>
              <td><code>R</code></td>
              <td>Redistribución</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Movimientos internos entre cuentas propias</td>
            </tr>
            <tr>
              <td><code>VDP</code></td>
              <td>Valores a Depositar</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques de tercero depositados, pendientes de acreditación bancaria</td>
            </tr>
            <tr>
              <td><code>VDB</code></td>
              <td>Valores a Debitar</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques propios entregados, pendientes de débito bancario</td>
            </tr>
            <tr>
              <td><code>VCB</code></td>
              <td>Valores a Cobrar</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques a cobrar (pendientes de gestión)</td>
            </tr>
            <tr>
              <td><code>VRP</code></td>
              <td>Valores Rechazados Propios</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques propios rechazados por el banco</td>
            </tr>
            <tr>
              <td><code>VRT</code></td>
              <td>Valores Rechazados Terceros</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques de tercero rechazados</td>
            </tr>
            <tr>
              <td><code>VVP</code></td>
              <td>Valores Vencidos Propios</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques propios vencidos sin presentar</td>
            </tr>
            <tr>
              <td><code>VVT</code></td>
              <td>Valores Vencidos Terceros</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques de tercero vencidos</td>
            </tr>
            <tr>
              <td><code>VAN</code></td>
              <td>Valores Anulados</td>
              <td>Otras (<code>O</code>)</td>
              <td>NO</td>
              <td>Cheques anulados (propios o de tercero)</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="my-4"></v-divider>

        <!-- ALTA DE CHEQUERA -->
        <p class="subtitle-1 font-weight-bold mb-2">Cómo dar de alta una chequera para una Cuenta Corriente</p>
        <p class="body-2 mb-3">
          Una <strong>chequera</strong> es una cuenta de tipo <code>Cheques (C)</code> con sub-tipo <code>Cheque (CD)</code> vinculada a una cuenta bancaria Cuenta Corriente (<code>B/CC</code>).
          Al emitir un cheque propio, el sistema utiliza la chequera como cuenta de origen y debita automáticamente la cuenta corriente bancaria padre al momento del débito.
        </p>
        <v-expansion-panels accordion flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-medium">
              <span><v-icon small color="blue" class="mr-2">list_alt</v-icon>Pasos para crear la chequera</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="60">Paso 1</td>
                    <td>
                      Verificar que existe una cuenta de tipo <strong>Banco (<code>B</code>)</strong> con sub-tipo <strong>Cuenta Corriente (<code>CC</code>)</strong> para el emisor correspondiente.
                      Si no existe, crearla primero seleccionando banco, emisor y sub-tipo <code>CC</code>.
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Paso 2</td>
                    <td>
                      Crear una nueva cuenta con los siguientes valores:
                      <ul class="mt-1">
                        <li><strong>Tipo:</strong> Cheques (<code>C</code>)</li>
                        <li><strong>Sub-tipo:</strong> Cheque (<code>CD</code>)</li>
                        <li><strong>Cuenta padre:</strong> seleccionar la cuenta <code>B/CC</code> del paso 1</li>
                        <li><strong>Emisor:</strong> el mismo emisor de la cuenta bancaria</li>
                        <li><strong>Nombre:</strong> nombre descriptivo (ej.: "Chequera Banco Nación")</li>
                        <li><strong>Representa fondos:</strong> SI</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Paso 3</td>
                    <td>
                      La chequera queda disponible en el módulo de Cheques para emitir cheques propios.
                      Al realizar el débito de un cheque propio, el sistema genera automáticamente el movimiento de tesorería:
                      <br><code>D: VDB (Valores a Debitar) | H: cuenta B/CC padre</code>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p class="caption mt-3 blue--text text--darken-2">
                Una cuenta corriente bancaria puede tener múltiples chequeras (por ejemplo, una por chequera física distinta).
              </p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    show: Boolean,
  },
};
</script>
